import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner } from '../components/ui';

const CERT_LABELS = {
  epc: 'EPC', gas_safety: 'Gas Safety Certificate', eicr: 'EICR',
  smoke_alarm: 'Smoke Alarm Check', co_alarm: 'CO Alarm Check',
  legionella: 'Legionella Risk Assessment',
};

const DOC_LABELS = {
  epc: 'EPC', gas_safety: 'Gas Safety Certificate', how_to_rent: 'How to Rent Guide',
  info_sheet: 'Information Sheet (RRA 2025)', right_to_rent: 'Right to Rent Check',
  tenancy_agreement: 'Tenancy Agreement',
};

function formatDate(d) {
  if (!d) return 'Not recorded';
  return new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' });
}

function certStatus(cert) {
  if (!cert) return { label:'Not recorded', color:'#dc2626' };
  const expiry = new Date(cert.expiry_date);
  const days = Math.ceil((expiry - new Date()) / 86400000);
  if (days < 0)  return { label:`Expired ${formatDate(cert.expiry_date)}`, color:'#dc2626' };
  if (days < 30) return { label:`Expires ${formatDate(cert.expiry_date)} (${days} days)`, color:'#d97706' };
  return { label:`Valid until ${formatDate(cert.expiry_date)}`, color:'#16a34a' };
}

export default function ComplianceReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  const token = () => localStorage.getItem('token');
  const headers = () => { const ac = localStorage.getItem('lc_access_code'); return { Authorization:`Bearer ${token()}`, ...(ac ? {'X-Access-Code': ac} : {}) }; };

  useEffect(() => {
    Promise.all([
      fetch(`/api/properties/${id}`,              { headers: headers() }).then(r => r.json()),
      fetch(`/api/properties/${id}/certificates`, { headers: headers() }).then(r => r.json()),
      fetch(`/api/properties/${id}/tenants`,      { headers: headers() }).then(r => r.json()),
      fetch(`/api/properties/${id}/deposits`,     { headers: headers() }).then(r => r.json()),
      fetch(`/api/properties/${id}/document-serving`, { headers: headers() }).then(r => r.json()),
      fetch('/api/profile',                        { headers: headers() }).then(r => r.json()),
      fetch(`/api/properties/${id}/rent-reviews`,  { headers: headers() }).then(r => r.json()),
      fetch(`/api/properties/${id}/pet-requests`,  { headers: headers() }).then(r => r.json()),
      fetch(`/api/properties/${id}/possession-notices`, { headers: headers() }).then(r => r.json()),
    ]).then(([property, certs, tenants, deposits, docs, profile, rentReviews, petRequests, possessionNotices]) => {
      setData({ property, certs: Array.isArray(certs) ? certs : [],
        tenants: Array.isArray(tenants) ? tenants : [],
        deposits: Array.isArray(deposits) ? deposits : [],
        docs: Array.isArray(docs) ? docs : [],
        profile,
        rentReviews: Array.isArray(rentReviews) ? rentReviews : [],
        petRequests: Array.isArray(petRequests) ? petRequests : [],
        possessionNotices: Array.isArray(possessionNotices) ? possessionNotices : [] });
      setLoading(false);
    });
  }, [id]);

  useEffect(() => {
    if (!loading && data) {
      setTimeout(() => window.print(), 500);
    }
  }, [loading, data]);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh' }}>
      <Spinner />
    </div>
  );

  const { property, certs, tenants, deposits, docs, profile, rentReviews, petRequests, possessionNotices } = data;
  const certsByType = {};
  certs.forEach(c => { if (!certsByType[c.cert_type]) certsByType[c.cert_type] = c; });

  const generatedDate = new Date().toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' });

  return (
    <div id="report">
      <style>{`
        @media print {
          body { margin: 0; }
          .no-print { display: none !important; }
          #report { padding: 0; }
        }
        body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #1a1a18; background: #fff; margin: 0; }
        #report { max-width: 800px; margin: 0 auto; padding: 40px; }
        .header { border-bottom: 3px solid #1a1a18; padding-bottom: 24px; margin-bottom: 32px; display: flex; justify-content: space-between; align-items: flex-start; }
        .logo { font-size: 22px; font-weight: 300; }
        .logo em { color: #1B4332; font-style: italic; }
        .meta { text-align: right; font-size: 12px; color: #6b7280; }
        h1 { font-size: 28px; font-weight: 300; margin: 0 0 4px; }
        h2 { font-size: 16px; font-weight: 700; margin: 0 0 16px; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb; text-transform: uppercase; letter-spacing: .05em; color: #374151; }
        .section { margin-bottom: 32px; }
        .property-meta { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; background: #f9fafb; border-radius: 8px; padding: 20px; margin-bottom: 32px; }
        .meta-item label { font-size: 11px; text-transform: uppercase; letter-spacing: .05em; color: #6b7280; display: block; margin-bottom: 2px; }
        .meta-item span { font-size: 14px; font-weight: 500; }
        table { width: 100%; border-collapse: collapse; font-size: 13px; }
        th { text-align: left; padding: 8px 12px; background: #f3f4f6; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: .05em; color: #374151; }
        td { padding: 10px 12px; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
        .status-ok      { color: #16a34a; font-weight: 600; }
        .status-warn    { color: #d97706; font-weight: 600; }
        .status-bad     { color: #dc2626; font-weight: 600; }
        .badge { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: 600; }
        .badge-ok   { background: #dcfce7; color: #16a34a; }
        .badge-warn { background: #fef3c7; color: #d97706; }
        .badge-bad  { background: #fee2e2; color: #dc2626; }
        .footer { margin-top: 48px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #9ca3af; display: flex; justify-content: space-between; }
        .summary-box { background: #f0fdf4; border: 1px solid #86efac; border-radius: 8px; padding: 16px 20px; margin-bottom: 24px; }
        .summary-box h3 { margin: 0 0 8px; font-size: 14px; color: #15803d; }
        .summary-box p { margin: 0; font-size: 13px; color: #166534; }
        .disclaimer { background: #fffbeb; border: 1px solid #fcd34d; border-radius: 8px; padding: 12px 16px; margin-top: 24px; font-size: 12px; color: #92400e; }
        .no-print-bar { background: #1a1a18; color: #fff; padding: 12px 24px; display: flex; justify-content: space-between; align-items: center; position: fixed; top: 0; left: 0; right: 0; z-index: 100; }
        .no-print-bar button { background: #fff; color: #1a1a18; border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; margin-left: 8px; }
        .no-print-bar button.secondary { background: rgba(255,255,255,.2); color: #fff; }
      `}</style>

      {/* Print toolbar */}
      <div className="no-print no-print-bar">
        <span style={{ fontWeight:300, fontSize:16 }}>Landlord<em style={{ color:'#6ee7b7' }}>Comply</em> — Compliance Report</span>
        <div>
          <button className="secondary" onClick={() => { try { window.close(); } catch(e) {} setTimeout(() => { window.location.href='/properties'; }, 300); }}>✕ Close</button>
          <button onClick={() => window.print()}>🖨️ Print / Save PDF</button>
        </div>
      </div>
      <div className="no-print" style={{ height:52 }} />

      {/* Report header */}
      <div className="header">
        <div>
          <div className="logo">Landlord<em>Comply</em></div>
          <h1 style={{ marginTop:8 }}>Compliance Report</h1>
          <p style={{ margin:0, fontSize:14, color:'#6b7280' }}>{property.address_line1}{property.address_line2 ? `, ${property.address_line2}` : ''}, {property.city}, {property.postcode}</p>
        </div>
        <div className="meta">
          <p style={{ margin:'0 0 4px', fontWeight:600 }}>Generated: {generatedDate}</p>
          {profile?.full_name && <p style={{ margin:0 }}>Prepared by: {profile.full_name}</p>}
          {profile?.company_name && <p style={{ margin:0 }}>{profile.company_name}</p>}
        </div>
      </div>

      {/* Property details */}
      <div className="property-meta">
        <div className="meta-item"><label>Property type</label><span>{property.property_type?.replace('_', ' ') || 'N/A'}</span></div>
        <div className="meta-item"><label>Bedrooms</label><span>{property.bedrooms || 'N/A'}</span></div>
        <div className="meta-item"><label>Occupants</label><span>{property.occupants || 'N/A'}</span></div>
        <div className="meta-item"><label>HMO</label><span>{property.is_hmo ? 'Yes' : 'No'}</span></div>
        <div className="meta-item"><label>Has gas</label><span>{property.has_gas ? 'Yes' : 'No'}</span></div>
        <div className="meta-item"><label>Water heating</label><span>{property.water_heating?.replace('_', ' ') || 'N/A'}</span></div>
      </div>

      {/* Compliance summary */}
      {(() => {
        const requiredCerts = ['epc','eicr','smoke_alarm','co_alarm'];
        if (property.has_gas) requiredCerts.push('gas_safety');
        const allValid = requiredCerts.every(t => {
          const c = certsByType[t];
          if (!c) return false;
          return new Date(c.expiry_date) > new Date();
        });
        return allValid ? (
          <div className="summary-box">
            <h3>✅ Property is compliant as of {generatedDate}</h3>
            <p>All required certificates are valid. This report can be used as evidence of compliance for mortgage applications, insurance, or tenant disputes.</p>
          </div>
        ) : (
          <div style={{ background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:8, padding:'16px 20px', marginBottom:24 }}>
            <h3 style={{ margin:'0 0 8px', fontSize:14, color:'#dc2626' }}>⚠️ Compliance gaps identified</h3>
            <p style={{ margin:0, fontSize:13, color:'#991b1b' }}>One or more required certificates are missing or expired. Action required.</p>
          </div>
        );
      })()}

      {/* Certificates */}
      <div className="section">
        <h2>Certificates & Safety Checks</h2>
        <table>
          <thead>
            <tr>
              <th>Certificate</th>
              <th>Issue date</th>
              <th>Expiry date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(CERT_LABELS).map(([type, label]) => {
              const cert = certsByType[type];
              const s = certStatus(cert);
              const cls = s.color === '#16a34a' ? 'status-ok' : s.color === '#d97706' ? 'status-warn' : 'status-bad';
              const badgeCls = s.color === '#16a34a' ? 'badge-ok' : s.color === '#d97706' ? 'badge-warn' : 'badge-bad';
              return (
                <tr key={type}>
                  <td style={{ fontWeight:500 }}>{label}</td>
                  <td>{formatDate(cert?.issued_date)}</td>
                  <td>{formatDate(cert?.expiry_date)}</td>
                  <td><span className={`badge ${badgeCls}`}>{s.label}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Tenants */}
      {tenants.length > 0 && (
        <div className="section">
          <h2>Tenants & Right to Rent</h2>
          <table>
            <thead>
              <tr><th>Name</th><th>Right to Rent check</th><th>Tenancy start</th><th>Tenancy end</th></tr>
            </thead>
            <tbody>
              {tenants.map(t => (
                <tr key={t.id}>
                  <td style={{ fontWeight:500 }}>{t.full_name}</td>
                  <td>{t.check_date ? <span className="badge badge-ok">Checked {formatDate(t.check_date)}</span> : <span className="badge badge-bad">Not recorded</span>}</td>
                  <td>{formatDate(t.tenancy_start)}</td>
                  <td>{t.tenancy_end ? formatDate(t.tenancy_end) : 'Ongoing'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Deposits */}
      {deposits.length > 0 && (
        <div className="section">
          <h2>Deposit Compliance</h2>
          <table>
            <thead>
              <tr><th>Tenant</th><th>Amount</th><th>Scheme</th><th>Protected</th><th>Prescribed info</th></tr>
            </thead>
            <tbody>
              {deposits.map(d => (
                <tr key={d.id}>
                  <td>{d.tenant_name || '—'}</td>
                  <td>£{(d.amount_gbp / 100).toFixed(2)}</td>
                  <td>{d.scheme?.toUpperCase()}</td>
                  <td>{d.protected_date ? <span className="badge badge-ok">{formatDate(d.protected_date)}</span> : <span className="badge badge-bad">Not recorded</span>}</td>
                  <td>{d.prescribed_info_date ? <span className="badge badge-ok">{formatDate(d.prescribed_info_date)}</span> : <span className="badge badge-bad">Not recorded</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Documents served */}
      {docs.length > 0 && (
        <div className="section">
          <h2>Documents Served</h2>
          <table>
            <thead>
              <tr><th>Document</th><th>Date served</th><th>Method</th><th>Tenant</th></tr>
            </thead>
            <tbody>
              {docs.map(d => (
                <tr key={d.id}>
                  <td style={{ fontWeight:500 }}>{d.doc_label || d.doc_type}</td>
                  <td><span className="badge badge-ok">{formatDate(d.served_date)}</span></td>
                  <td>{d.method_label || d.method}</td>
                  <td>{d.tenant_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Check for missing required docs */}
          {(() => {
            const served = new Set(docs.map(d => d.doc_type));
            const missing = ['epc','gas_safety','how_to_rent','info_sheet'].filter(t => !served.has(t));
            if (missing.length === 0) return null;
            return (
              <div style={{ marginTop:12, padding:'10px 14px', background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:6 }}>
                <p style={{ margin:0, fontSize:12, color:'#9a3412', fontWeight:600 }}>
                  ⚠️ No serving record for: {missing.map(t => DOC_LABELS[t]).join(', ')}
                </p>
              </div>
            );
          })()}
        </div>
      )}

      {/* Disclaimer */}
      {/* RRA Compliance */}
      <div className="section">
        <h2>Renters' Rights Act 2025 Compliance</h2>

        {/* Information Sheet */}
        <table style={{ marginBottom:16 }}>
          <thead><tr><th>RRA Requirement</th><th>Tenant</th><th>Status</th><th>Date</th></tr></thead>
          <tbody>
            {tenants.length === 0 ? (
              <tr><td colSpan={4} style={{ color:'#6b7280', fontStyle:'italic' }}>No tenants recorded</td></tr>
            ) : tenants.map(t => (
              <tr key={t.id}>
                <td style={{ fontWeight:500 }}>Information Sheet</td>
                <td>{t.full_name}</td>
                <td>
                  {t.info_sheet_sent
                    ? <span className="badge badge-ok">Sent</span>
                    : <span className="badge badge-bad">Not recorded</span>}
                </td>
                <td>{t.info_sheet_sent_date ? formatDate(t.info_sheet_sent_date) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Rent Reviews */}
        {rentReviews.length > 0 && (
          <>
            <p style={{ fontWeight:600, fontSize:13, margin:'16px 0 8px' }}>Section 13 Rent Reviews</p>
            <table style={{ marginBottom:16 }}>
              <thead><tr><th>Notice date</th><th>New rent</th><th>Effective date</th><th>Status</th></tr></thead>
              <tbody>
                {rentReviews.map(r => (
                  <tr key={r.id}>
                    <td>{formatDate(r.notice_date)}</td>
                    <td>{r.new_rent_gbp ? `£${(r.new_rent_gbp/100).toFixed(0)}/mo` : '—'}</td>
                    <td>{formatDate(r.effective_date)}</td>
                    <td><span className="badge badge-ok">Served</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Pet Requests */}
        {petRequests.length > 0 && (
          <>
            <p style={{ fontWeight:600, fontSize:13, margin:'16px 0 8px' }}>Pet Requests</p>
            <table style={{ marginBottom:16 }}>
              <thead><tr><th>Tenant</th><th>Pet type</th><th>Decision</th><th>Date</th></tr></thead>
              <tbody>
                {petRequests.map(r => (
                  <tr key={r.id}>
                    <td>{r.tenant_name || '—'}</td>
                    <td>{r.pet_type || '—'}</td>
                    <td>
                      <span className={`badge ${r.decision === 'approved' ? 'badge-ok' : r.decision === 'declined' ? 'badge-bad' : 'badge-warn'}`}>
                        {r.decision || 'Pending'}
                      </span>
                    </td>
                    <td>{formatDate(r.request_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Possession Notices */}
        {possessionNotices.length > 0 && (
          <>
            <p style={{ fontWeight:600, fontSize:13, margin:'16px 0 8px' }}>Possession Notices</p>
            <table style={{ marginBottom:16 }}>
              <thead><tr><th>Tenant</th><th>Ground</th><th>Notice date</th><th>Hearing date</th></tr></thead>
              <tbody>
                {possessionNotices.map(n => (
                  <tr key={n.id}>
                    <td>{n.tenant_name || '—'}</td>
                    <td>{n.ground || '—'}</td>
                    <td>{formatDate(n.notice_date)}</td>
                    <td>{n.hearing_date ? formatDate(n.hearing_date) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {rentReviews.length === 0 && petRequests.length === 0 && possessionNotices.length === 0 && (
          <p style={{ fontSize:13, color:'#6b7280', fontStyle:'italic' }}>No Section 13 notices, pet requests or possession notices recorded.</p>
        )}
      </div>

      <div className="disclaimer">
        <strong>Important:</strong> This report is generated from data entered into LandlordComply and is provided for reference purposes only.
        It does not constitute legal advice. Always verify compliance requirements with a qualified professional.
        LandlordComply Ltd accepts no liability for inaccuracies in user-entered data.
      </div>

      {/* Footer */}
      <div className="footer">
        <span>LandlordComply — UK Compliance Hub — landlordcomply.uk</span>
        <span>Report generated {generatedDate}</span>
      </div>
    </div>
  );
}
